import { useEffect, useState } from 'react';

const POLL_MS = 300;
const STABLE_MS = 800;
const MAX_WAIT_MS = 90000;
const LOADING_SELECTOR = '.MuiCircularProgress-root, .MuiLinearProgress-root';

const countDashboardWidgetProgress = (root, expectedWidgetCount = 0) => {
    const widgets = root.querySelectorAll('[data-dashboard-widget]');
    const renderedWidgets = widgets.length;
    let loadedWidgets = 0;

    widgets.forEach((widget) => {
        if (!widget.querySelector(LOADING_SELECTOR)) {
            loadedWidgets += 1;
        }
    });

    const missingWidgets = expectedWidgetCount > 0 ? Math.max(expectedWidgetCount - renderedWidgets, 0) : 0;
    const loadingIndicators = root.querySelectorAll(LOADING_SELECTOR).length;
    const pending =
        loadingIndicators > 0
        || missingWidgets > 0
        || (expectedWidgetCount > 0 && loadedWidgets < expectedWidgetCount);

    return {
        renderedWidgets,
        loadedWidgets,
        loadingIndicators,
        missingWidgets,
        pending,
    };
};

export const useDashboardReportChartsReady = (containerRef, { enabled = false, expectedWidgetCount = 0 } = {}) => {
    const [ready, setReady] = useState(false);
    const [timedOut, setTimedOut] = useState(false);
    const [progress, setProgress] = useState({ loadedWidgets: 0, expectedWidgetCount: 0 });

    useEffect(() => {
        if (!enabled) {
            return undefined;
        }

        let cancelled = false;
        let stableSince = null;
        const startedAt = Date.now();
        let pollTimer = null;

        const tick = () => {
            if (cancelled) return;

            const root = containerRef?.current;
            if (!root) {
                pollTimer = setTimeout(tick, POLL_MS);
                return;
            }

            const status = countDashboardWidgetProgress(root, expectedWidgetCount);
            const elapsed = Date.now() - startedAt;

            setProgress({
                loadedWidgets: status.loadedWidgets,
                expectedWidgetCount,
            });

            if (!status.pending) {
                if (!stableSince) {
                    stableSince = Date.now();
                }
                if (Date.now() - stableSince >= STABLE_MS) {
                    setReady(true);
                    return;
                }
            } else {
                stableSince = null;
                setReady(false);
            }

            if (elapsed >= MAX_WAIT_MS) {
                setTimedOut(true);
                setReady(true);
                return;
            }

            pollTimer = setTimeout(tick, POLL_MS);
        };

        const root = containerRef?.current;
        if (root) {
            const status = countDashboardWidgetProgress(root, expectedWidgetCount);
            setProgress({
                loadedWidgets: status.loadedWidgets,
                expectedWidgetCount,
            });
            if (!status.pending) {
                setReady(true);
                return undefined;
            }
        }

        setReady(false);
        setTimedOut(false);
        pollTimer = setTimeout(tick, POLL_MS);

        return () => {
            cancelled = true;
            if (pollTimer) clearTimeout(pollTimer);
        };
    }, [containerRef, enabled, expectedWidgetCount]);

    return { ready, timedOut, progress };
};
